import { buildCanvasFontString, sanitizeTextLayerFontFamily } from '../fonts/textLayerFonts';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const toRadians = (degrees) => (degrees * Math.PI) / 180;
const primitiveColorRoles = new Set(['primary', 'secondary', 'textUpper', 'textLower']);
const primitiveColorModes = new Set(['theme', 'custom']);
const textSourceSet = new Set(['main', 'sub', 'generic']);
const hexColorPattern = /^#([0-9a-f]{6}|[0-9a-f]{3})$/i;

const withDefaultNumber = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeTextSource = (value, fallback = 'main') => (
  textSourceSet.has(value) ? value : fallback
);

const normalizePrimitiveColorRole = (role, fallback = 'primary') => (
  primitiveColorRoles.has(role) ? role : fallback
);

const normalizePrimitiveColorMode = (mode, fallback = 'theme') => (
  primitiveColorModes.has(mode) ? mode : fallback
);

const normalizePrimitiveColorValue = (value, fallback = '#ffffff') => (
  typeof value === 'string' && hexColorPattern.test(value) ? value : fallback
);

const resolveThemeColor = (role, config) => {
  if (role === 'secondary') return config.colorSecondary;
  if (role === 'textUpper') return config.colorTextUpper;
  if (role === 'textLower') return config.colorTextLower;
  return config.colorPrimary;
};

const resolvePrimitiveChannelColor = (shape, config, channel) => {
  if (!shape) return '#ffffff';
  const legacyRole = normalizePrimitiveColorRole(shape.colorRole, 'primary');
  const roleField = channel === 'stroke' ? 'strokeColorRole' : 'fillColorRole';
  const modeField = channel === 'stroke' ? 'strokeColorMode' : 'fillColorMode';
  const valueField = channel === 'stroke' ? 'strokeColorValue' : 'fillColorValue';
  const colorRole = normalizePrimitiveColorRole(shape[roleField], legacyRole);
  const colorMode = normalizePrimitiveColorMode(shape[modeField], 'theme');
  const customValue = normalizePrimitiveColorValue(shape[valueField], resolveThemeColor(colorRole, config));
  return colorMode === 'custom' ? customValue : resolveThemeColor(colorRole, config);
};

export const resolvePrimitiveFillColor = (shape, config) => (
  resolvePrimitiveChannelColor(shape, config, 'fill')
);

export const resolvePrimitiveStrokeColor = (shape, config) => (
  resolvePrimitiveChannelColor(shape, config, 'stroke')
);

export const unionBounds = (boundsList) => {
  const valid = boundsList.filter(Boolean);
  if (!valid.length) return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  const minX = Math.min(...valid.map((bounds) => bounds.minX));
  const minY = Math.min(...valid.map((bounds) => bounds.minY));
  const maxX = Math.max(...valid.map((bounds) => bounds.maxX));
  const maxY = Math.max(...valid.map((bounds) => bounds.maxY));
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
};

export const rectToBounds = (x, y, width, height) => ({
  minX: x,
  minY: y,
  maxX: x + width,
  maxY: y + height,
  width,
  height,
});

const getBoundsFromPoints = (points) => {
  if (!points.length) return rectToBounds(0, 0, 0, 0);
  const minX = Math.min(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxX = Math.max(...points.map((point) => point.x));
  const maxY = Math.max(...points.map((point) => point.y));
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
};

const rotatePoint = (point, pivot, angleRadians) => {
  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;
  const sin = Math.sin(angleRadians);
  const cos = Math.cos(angleRadians);
  return {
    x: pivot.x + dx * cos - dy * sin,
    y: pivot.y + dx * sin + dy * cos,
  };
};

const rotateBounds = (bounds, pivot, angleRadians) => {
  if (!bounds) return null;
  if (!angleRadians) return { ...bounds };
  return getBoundsFromPoints([
    rotatePoint({ x: bounds.minX, y: bounds.minY }, pivot, angleRadians),
    rotatePoint({ x: bounds.maxX, y: bounds.minY }, pivot, angleRadians),
    rotatePoint({ x: bounds.maxX, y: bounds.maxY }, pivot, angleRadians),
    rotatePoint({ x: bounds.minX, y: bounds.maxY }, pivot, angleRadians),
  ]);
};

const getParallelogramPoints = (x, y, width, height, skewX = 0) => ([
  { x, y },
  { x: x + width, y },
  { x: x + width + skewX, y: y + height },
  { x: x + skewX, y: y + height },
]);

export const traceParallelogramPath = (ctx, x, y, width, height, skewX = 0) => {
  const points = getParallelogramPoints(x, y, width, height, skewX);
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i += 1) ctx.lineTo(points[i].x, points[i].y);
  ctx.closePath();
};

const estimateTextWidth = (text, fontPx) => ((text?.length || 0) * Math.max(0, fontPx) * 0.56);

const measureTextWidth = (ctx, text, font) => {
  if (!ctx || !text) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
};

const fitFontToWidth = ({ ctx, text, preferredPx, minPx, maxWidth, weight = 'bold', family = null }) => {
  const initial = Math.max(minPx, preferredPx);
  if (!text || !maxWidth || maxWidth <= 0) return initial;
  const font = buildCanvasFontString({ weight, px: initial, family });
  const measured = ctx ? measureTextWidth(ctx, text, font) : estimateTextWidth(text, initial);
  if (!measured || measured <= maxWidth) return initial;
  return clamp((initial * maxWidth) / measured, minPx, initial);
};

const getGenericText = (person, presetId, primitiveId, fallbackText) => {
  const bucket = person?.genericTextByPresetId?.[presetId];
  if (!bucket || typeof bucket !== 'object') return fallbackText;
  return typeof bucket[primitiveId] === 'string' ? bucket[primitiveId] : fallbackText;
};

const buildTextLayout = ({
  ctx,
  shape,
  baseBounds,
  scale,
  mainText,
  subText,
  mainFontPx,
  subFontPx,
  activePresetId,
  person,
}) => {
  if (shape.layerKind === 'text') {
    const textSource = normalizeTextSource(shape.textSource, 'main');
    const resolvedText = textSource === 'main'
      ? mainText
      : textSource === 'sub'
        ? subText
        : getGenericText(person, activePresetId, shape.id, shape.textValue || '');
    const preferredPx = Math.max(8, baseBounds.height * withDefaultNumber(shape.textFontRatio, textSource === 'main' ? 0.8 : 0.7));
    const fontFamily = sanitizeTextLayerFontFamily(shape.textFontFamily);
    const fontWeight = textSource === 'main' ? 'bold' : '600';
    const paddingX = clamp(withDefaultNumber(shape.textPaddingX, 0), 0, Math.max(0, baseBounds.width - 4));
    const maxWidth = Math.max(8, baseBounds.width - paddingX * 2);
    const fontPx = fitFontToWidth({
      ctx,
      text: resolvedText,
      preferredPx,
      minPx: Math.max(8, preferredPx * 0.35),
      maxWidth,
      weight: fontWeight,
      family: fontFamily,
    });
    const font = buildCanvasFontString({ weight: fontWeight, px: fontPx, family: fontFamily });
    const width = ctx ? measureTextWidth(ctx, resolvedText, font) : estimateTextWidth(resolvedText, fontPx);
    const y = baseBounds.minY + baseBounds.height * clamp(withDefaultNumber(shape.textBaselineRatio, 0.78), 0.2, 1.2);
    const x = baseBounds.minX + paddingX;
    return {
      lines: [{ kind: textSource, text: resolvedText, font, fontPx, x, y, width }],
      localBounds: rectToBounds(x, y - fontPx, Math.max(1, width), fontPx + Math.max(4, 6 * scale)),
    };
  }

  if (shape.mainTextVisible !== true && shape.subTextVisible !== true) return null;
  const paddingX = clamp(baseBounds.width * 0.08, Math.max(6, 6 * scale), Math.max(24, 24 * scale));
  const maxWidth = Math.max(10, baseBounds.width - paddingX * 2);
  const lines = [];
  if (shape.mainTextVisible === true) {
    const fontPx = fitFontToWidth({ ctx, text: mainText, preferredPx: Math.min(mainFontPx, baseBounds.height * 0.36), minPx: Math.max(12, 10 * scale), maxWidth, weight: 'bold' });
    const font = `bold ${fontPx}px Arial`;
    const width = ctx ? measureTextWidth(ctx, mainText, font) : estimateTextWidth(mainText, fontPx);
    lines.push({ kind: 'main', text: mainText, font, fontPx, x: baseBounds.minX + paddingX, y: baseBounds.minY + baseBounds.height * (shape.subTextVisible === true ? 0.48 : 0.65), width });
  }
  if (shape.subTextVisible === true) {
    const fontPx = fitFontToWidth({ ctx, text: subText, preferredPx: Math.min(subFontPx, baseBounds.height * 0.26), minPx: Math.max(10, 8 * scale), maxWidth, weight: '600' });
    const font = `600 ${fontPx}px Arial`;
    const width = ctx ? measureTextWidth(ctx, subText, font) : estimateTextWidth(subText, fontPx);
    lines.push({ kind: 'sub', text: subText, font, fontPx, x: baseBounds.minX + paddingX, y: baseBounds.minY + baseBounds.height * (shape.mainTextVisible === true ? 0.82 : 0.65), width });
  }
  return {
    lines,
    localBounds: unionBounds(lines.map((line) => rectToBounds(line.x, line.y - line.fontPx, line.width, line.fontPx + Math.max(6, 8 * scale)))),
  };
};

export const getDurationsForConfig = (targetFps, config) => {
  const resolvedFps = Math.max(1, targetFps);
  const transitionInFrames = Math.max(1, Math.round(config.transitionInDuration * resolvedFps));
  const transitionOutFrames = Math.max(1, Math.round(config.transitionOutDuration * resolvedFps));
  const stillFrames = Math.max(0, Math.round(config.stillDuration * resolvedFps));
  const fadeInFrames = Math.max(1, Math.round(Math.max(config.fadeInDuration ?? (config.transitionInDuration / 2), 0.05) * resolvedFps));
  const fadeOutFrames = Math.max(1, Math.round(Math.max(config.fadeOutDuration ?? (config.transitionOutDuration / 2), 0.05) * resolvedFps));
  return { transitionInFrames, transitionOutFrames, stillFrames, fadeInFrames, fadeOutFrames, totalFrames: transitionInFrames + transitionOutFrames + stillFrames };
};

export const getRenderSceneMetrics = ({ ctx, person, config, activePresetId }) => {
  const scale = Math.max(0.1, withDefaultNumber(config.scale, 1));
  const rotation = clamp(withDefaultNumber(config.rotation, 0), -180, 180);
  const globalRotationRadians = toRadians(rotation);
  const mainText = person?.name || 'Name';
  const subText = person?.title || 'Title';
  const mainFontPx = 60 * scale;
  const subFontPx = 40 * scale;
  const mainFont = `bold ${mainFontPx}px Arial`;
  const subFont = `${subFontPx}px Arial`;
  const mainTextWidth = ctx ? measureTextWidth(ctx, mainText, mainFont) : estimateTextWidth(mainText, mainFontPx);
  const subTextWidth = ctx ? measureTextWidth(ctx, subText, subFont) : estimateTextWidth(subText, subFontPx);

  const classic = { radius: clamp(withDefaultNumber(config.classicRadius, 10), 0, 60) * scale, accentWidth: clamp(withDefaultNumber(config.classicAccentWidth, 15), 0, 80) * scale, textPaddingX: clamp(withDefaultNumber(config.classicTextPaddingX, 40), 10, 120) * scale, mainTextY: 60 * scale, subTextY: 100 * scale };
  const minimal = { nameHeight: clamp(withDefaultNumber(config.minimalNameHeight, 70), 40, 140) * scale, titleHeight: clamp(withDefaultNumber(config.minimalTitleHeight, 50), 30, 120) * scale, textPaddingX: clamp(withDefaultNumber(config.minimalTextPaddingX, 20), 10, 80) * scale };
  minimal.nameWidth = mainTextWidth + minimal.textPaddingX * 2;
  minimal.titleWidth = subTextWidth + minimal.textPaddingX * 2;
  minimal.nameTextY = minimal.nameHeight * 0.78;
  minimal.titleTextY = minimal.nameHeight + minimal.titleHeight * 0.8;
  const modern = { skewOffset: clamp(withDefaultNumber(config.modernSkewOffset, 50), 0, 120) * scale, accentOffset: clamp(withDefaultNumber(config.modernAccentOffset, 30), 0, 80) * scale, accentWidth: clamp(withDefaultNumber(config.modernAccentWidth, 20), 0, 80) * scale, textX: clamp(withDefaultNumber(config.modernTextX, 80), 20, 200) * scale, mainTextY: 60 * scale, subTextY: 100 * scale, skewFactor: 0.2 };
  const edgeLink = { barX: -160 * scale, barWidth: Math.max(mainTextWidth, subTextWidth) + 160 * scale, bodyHeight: 118 * scale, tipX: Math.max(mainTextWidth, subTextWidth), tipWidth: 60 * scale, tipSkewX: -36 * scale, bodyWidth: Math.max(mainTextWidth, subTextWidth) + 220 * scale, accentX: -160 * scale, accentWidth: 18 * scale, textX: -92 * scale, mainTextY: 60 * scale, subTextY: 100 * scale };
  const logoBadge = { bodyX: 64 * scale, bodyWidth: Math.max(mainTextWidth, subTextWidth) + 188 * scale, bodyHeight: 120 * scale, accentX: 82 * scale, accentY: 12 * scale, accentWidth: 12 * scale, accentHeight: 96 * scale, accentRadius: 6 * scale, logoPlateX: -72 * scale, logoPlateY: 0, logoPlateSize: 120 * scale, logoImageX: -54 * scale, logoImageY: 18 * scale, logoImageWidth: 84 * scale, logoImageHeight: 84 * scale, textX: 132 * scale, mainTextY: 60 * scale, subTextY: 100 * scale };

  const classicBodyWidth = Math.max(mainTextWidth, subTextWidth) + classic.textPaddingX + 20 * scale;
  const classicBodyHeight = 120 * scale;
  const modernBodyWidth = Math.max(mainTextWidth, subTextWidth) + modern.textX + 20 * scale;
  const modernBodyHeight = 120 * scale;
  const templateShapeVisible = config.templateShapeVisible !== false;
  const templateMainTextVisible = config.templateMainTextVisible !== false;
  const templateSubTextVisible = config.templateSubTextVisible !== false;

  let templateShapeBounds;
  if (config.preset === 'minimal-box') templateShapeBounds = rectToBounds(0, 0, Math.max(minimal.nameWidth, minimal.titleWidth), minimal.nameHeight + minimal.titleHeight);
  else if (config.preset === 'modern-accent') templateShapeBounds = getBoundsFromPoints([{ x: modern.skewOffset, y: 0 }, { x: modern.skewOffset + modernBodyWidth, y: 0 }, { x: modern.skewOffset + modernBodyWidth - modern.skewFactor * modernBodyHeight, y: modernBodyHeight }, { x: modern.skewOffset - modern.skewFactor * modernBodyHeight, y: modernBodyHeight }]);
  else if (config.preset === 'edge-link') templateShapeBounds = unionBounds([rectToBounds(edgeLink.barX, 0, edgeLink.barWidth, edgeLink.bodyHeight), rectToBounds(edgeLink.accentX, 0, edgeLink.accentWidth, edgeLink.bodyHeight), getBoundsFromPoints(getParallelogramPoints(edgeLink.tipX, 0, edgeLink.tipWidth, edgeLink.bodyHeight, edgeLink.tipSkewX))]);
  else if (config.preset === 'logo-badge') templateShapeBounds = unionBounds([rectToBounds(logoBadge.bodyX, 0, logoBadge.bodyWidth, logoBadge.bodyHeight), rectToBounds(logoBadge.accentX, logoBadge.accentY, logoBadge.accentWidth, logoBadge.accentHeight), rectToBounds(logoBadge.logoPlateX, logoBadge.logoPlateY, logoBadge.logoPlateSize, logoBadge.logoPlateSize)]);
  else templateShapeBounds = rectToBounds(0, 0, classicBodyWidth, classicBodyHeight);

  const rotationPivot = { x: templateShapeBounds.minX + (templateShapeBounds.width / 2), y: templateShapeBounds.minY + (templateShapeBounds.height / 2) };
  const primitives = (Array.isArray(config.shapePrimitives) ? config.shapePrimitives : []).map((shape, index) => {
    const type = shape?.type || 'rect';
    const width = Math.max(8, withDefaultNumber(shape?.width, type === 'circle' ? 90 : 220));
    const height = type === 'circle' ? width : Math.max(1, withDefaultNumber(shape?.height, type === 'line' ? 4 : 80));
    const x = withDefaultNumber(shape?.x, 60);
    const y = withDefaultNumber(shape?.y, 40);
    const strokeWidth = clamp(withDefaultNumber(shape?.strokeWidth, 3), 1, 80);
    const baseBounds = type === 'line' ? rectToBounds(x, y - Math.max(strokeWidth, height) / 2, width, Math.max(strokeWidth, height)) : rectToBounds(x, y, width, height);
    const center = { x: baseBounds.minX + baseBounds.width / 2, y: baseBounds.minY + baseBounds.height / 2 };
    const shapeBounds = type === 'parallelogram' ? getBoundsFromPoints(getParallelogramPoints(x, y, width, height, withDefaultNumber(shape?.skewX, 0))) : baseBounds;
    const localBounds = rotateBounds(shapeBounds, center, toRadians(withDefaultNumber(shape?.rotation, 0)));
    const textLayout = buildTextLayout({ ctx, shape: shape || {}, baseBounds, scale, mainText, subText, mainFontPx, subFontPx, activePresetId, person });
    const hitLocalBounds = unionBounds([localBounds, textLayout?.localBounds]);
    return {
      ...shape,
      id: shape?.id || `shape-${index}`,
      type,
      x,
      y,
      width,
      height,
      baseBounds,
      rotation: withDefaultNumber(shape?.rotation, 0),
      strokeWidth,
      skewX: withDefaultNumber(shape?.skewX, 0),
      fill: shape?.layerKind === 'text' ? false : (shape?.fill !== false),
      stroke: shape?.layerKind === 'text' ? false : Boolean(shape?.stroke),
      visible: shape?.visible !== false,
      layerKind: shape?.layerKind === 'text' ? 'text' : (shape?.layerKind === 'image' || type === 'image' ? 'image' : 'shape'),
      cornerRadius: clamp(withDefaultNumber(shape?.cornerRadius, 16), 0, 540),
      textLayout,
      boundsWithGlobal: rotateBounds(localBounds, rotationPivot, globalRotationRadians),
      hitBoundsWithGlobal: rotateBounds(hitLocalBounds, rotationPivot, globalRotationRadians),
    };
  });

  return {
    scale,
    globalRotationRadians,
    rotationPivot,
    mainText,
    subText,
    mainTextWidth,
    subTextWidth,
    mainFont,
    subFont,
    templateShapeVisible,
    templateMainTextVisible,
    templateSubTextVisible,
    baseBoxWidth: config.preset === 'minimal-box' ? templateShapeBounds.width : (config.preset === 'modern-accent' ? modernBodyWidth : (config.preset === 'edge-link' ? edgeLink.bodyWidth : (config.preset === 'logo-badge' ? logoBadge.bodyWidth : classicBodyWidth))),
    baseBoxHeight: config.preset === 'minimal-box' ? templateShapeBounds.height : (config.preset === 'modern-accent' ? modernBodyHeight : (config.preset === 'edge-link' ? edgeLink.bodyHeight : (config.preset === 'logo-badge' ? logoBadge.bodyHeight : classicBodyHeight))),
    renderBounds: unionBounds([templateShapeVisible ? rotateBounds(templateShapeBounds, rotationPivot, globalRotationRadians) : null, ...primitives.map((shape) => (shape.visible === false ? null : shape.hitBoundsWithGlobal))]),
    classic,
    minimal,
    modern,
    edgeLink,
    logoBadge,
    primitives,
  };
};
